import React from "react";
import { Alert } from "@material-ui/lab";
import { Tooltip } from "@material-ui/core";
import { CopyToClipboard } from "react-copy-to-clipboard/lib/Component";
import { getServiceNameWithNickname } from "../../../utils/getServiceNameWithNickname";
import PageSpinner from "../../elements/spinner/PageSpinner";
import RenderStatus from "../../styles/styledStatus";
import { TimestampToDateMoment } from "../../../utils/timestampToDate";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";

import { StyledTooltip } from "../../styles/styledTooltip";
import {
  StyledCol,
  StyledScrollTable,
  StyledTable,
  StyledTableBody,
  StyledTableHead,
  StyledTableRow
} from "../../styles/styledTable";
import { StyledBreakWord } from "../../styles/styledBreakWord";
import { StyledCopy } from "../../styles/styledCopy";

const TransactionsList = ({ transactions, notification, setNotification }) => {
  const { t } = useTranslation("cashFlow");

  if (!transactions) {
    return <PageSpinner
      position="center"
      pt="20"
      pb="20"
    />;
  }

  if (transactions.length === 0) {
    return <Alert severity="warning">{t("transactions.table.emptyData")}</Alert>;
  }

  const showDetails = (e) => {
    if (e.target.closest(".stop-event")) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <>
      <StyledScrollTable className="transactions-table_scroll">
        <StyledTable
          width="3305"
          size="14"
          className="transactions-table"
        >
          <StyledTableHead
            scroll="auto"
            className="transactions-table__head"
          >
            <div>
              Id
              <Tooltip
                title={t("transactions.table.tooltips.id")}
                arrow
                placement="top"
              >
                <StyledTooltip className="icon-light-help" />
              </Tooltip>
            </div>
            <div>
              Reference id
              <Tooltip
                title={t("transactions.table.tooltips.referenceId")}
                arrow
                placement="top"
              >
                <StyledTooltip className="icon-light-help" />
              </Tooltip>
            </div>
            <div>
              {t("transactions.table.user")}
            </div>
            <div>
              {t("transactions.table.project")}
            </div>
            <div>
              {t("transactions.table.service")}
            </div>
            <div>
              {t("transactions.table.paymentMethod")}
            </div>
            <div>
              {t("transactions.table.currency")}
            </div>
            <div>
              {t("transactions.table.amount")}
              <Tooltip
                title={t("transactions.table.tooltips.amount")}
                arrow
                placement="top"
              >
                <StyledTooltip className="icon-light-help" />
              </Tooltip>
            </div>
            <div>
              {t("transactions.table.processedAmount")}
              <Tooltip
                title={t("transactions.table.tooltips.processedAmount")}
                arrow
                placement="top"
              >
                <StyledTooltip className="icon-light-help" />
              </Tooltip>
            </div>
            <div>
              {t("transactions.table.status")}
            </div>
            <div>
              {t("transactions.table.profit")}
            </div>
            <div>
              {t("transactions.table.rateProfit")}
            </div>
            <div>
              {t("transactions.table.date")}
            </div>
            <div>
              {t("transactions.table.type")}
            </div>
            <div>
              {t("transactions.table.paymentType")}
            </div>
            <div>
              {t("transactions.table.transactionType")}
            </div>
            <div>
              Related reference id
            </div>
            <div>
              {t("transactions.table.note")}
            </div>
          </StyledTableHead>
          <StyledTableBody>
            {transactions && transactions.map((value, key) => (
              <StyledTableRow
                key={key}
                scroll="auto"
                className="transactions-table__row"
                cursor="pointer"
                onClick={(e) => {showDetails(e);}}
                as={NavLink}
                to={`/transaction/${value.type}s/${value.id}`}
              >
                <StyledCol
                  inline
                  data-title="id"
                >
                  <CopyToClipboard
                    className="stop-event"
                    text={value.id}
                    onCopy={() => {
                      setNotification({ ...notification, visible: true, type: "success", message: t("transactions.table.copied") });
                    }}
                  >
                    <StyledBreakWord as="p">
                      {value.id} <StyledCopy
                      title={t("transactions.table.copy")}
                      className="icon-light-copy"
                    />
                    </StyledBreakWord>
                  </CopyToClipboard>
                </StyledCol>
                <StyledCol
                  inline
                  data-title="Reference id"
                >
                  <CopyToClipboard
                    className="stop-event"
                    text={value.referenceId}
                    onCopy={() => {
                      setNotification({ ...notification, visible: true, type: "success", message: t("transactions.table.copied") });
                    }}
                  >
                    <StyledBreakWord as="p">
                      {value.referenceId} <StyledCopy
                      title={t("transactions.table.copy")}
                      className="icon-light-copy"
                    />
                    </StyledBreakWord>
                  </CopyToClipboard>
                </StyledCol>
                <StyledCol data-title={t("transactions.table.user")} inline>
                  <StyledBreakWord>
                    {value.connection.project.user.nickname ? value.connection.project.user.nickname + ":" : ""} {value.connection.project.user.email}
                  </StyledBreakWord>
                </StyledCol>
                <StyledCol data-title={t("transactions.table.project")}>
                  {value.connection.project.name}
                </StyledCol>
                <StyledCol data-title={t("transactions.table.service")}>
                  {getServiceNameWithNickname(value.connection.service.name, value.connection.service.nickname)}
                </StyledCol>
                <StyledCol data-title={t("transactions.table.paymentMethod")}>
                  {value.paymentSystem.name}
                </StyledCol>
                <StyledCol data-title={t("transactions.table.currency")}>
                  {value.currency.asset}
                </StyledCol>
                <StyledCol data-title={t("transactions.table.amount")}>
                  {value.amount}
                </StyledCol>
                <StyledCol data-title={t("transactions.table.processedAmount")}>
                  {value.processedAmount}
                </StyledCol>
                <StyledCol
                  inline
                  data-title={t("transactions.table.status")}
                >
                  <RenderStatus status={value.status.value} />
                </StyledCol>
                <StyledCol data-title={t("transactions.table.profit")}>
                  {value.profit}
                </StyledCol>
                <StyledCol data-title={t("transactions.table.rateProfit")}>
                  {value.rateProfit}
                </StyledCol>
                <StyledCol data-title={t("transactions.table.date")}>
                  {TimestampToDateMoment(value.createdAt)}
                </StyledCol>
                <StyledCol data-title={t("transactions.table.type")}>
                  {value.type}
                </StyledCol>
                <StyledCol data-title={t("transactions.table.paymentType")}>
                  {value.paymentType}
                </StyledCol>
                <StyledCol data-title={t("transactions.table.transactionType")}>
                  {value.transactionType}
                </StyledCol>
                <StyledCol data-title="Related reference id">
                  {value.relatedReferenceId ?
                    <CopyToClipboard
                      className="stop-event"
                      text={value.relatedReferenceId}
                      onCopy={() => {
                        setNotification({
                          ...notification,
                          visible: true,
                          type: "success",
                          message: t("transactions.table.copied")
                        });
                      }}
                    >
                      <StyledBreakWord as="p">
                        {value.relatedReferenceId} <StyledCopy
                        title={t("transactions.table.copy")}
                        className="icon-light-copy"
                      />
                      </StyledBreakWord>
                    </CopyToClipboard>
                    : "--"}
                </StyledCol>
                <StyledCol
                  inline
                  data-title={t("transactions.table.note")}
                  className="transactions-table__note"
                >
                  <p className="note">
                    {value.note}
                  </p>
                  {value.note && value.note.length > 25 &&
                    <Tooltip
                      title={value.note}
                      arrow
                      placement="top"
                    >
                      <StyledTooltip className="icon-light-info" />
                    </Tooltip>}
                </StyledCol>
              </StyledTableRow>
            ))}
          </StyledTableBody>
        </StyledTable>
      </StyledScrollTable>
    </>
  );
};

export default TransactionsList;